import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, AlertCircleIcon, CheckCircle2Icon, LockIcon, ClockIcon, CreditCardIcon, QrCodeIcon, BanknoteIcon } from 'lucide-react';
import { mockShifts, ShiftStatus, ShiftTxnMethod, ShiftTxnStatus } from '../mocks/shiftData';
import { formatMoney } from '../../../shared/config/localeProfile';
const METHOD_LABELS: Record<ShiftTxnMethod, string> = {
  card: 'Credit/Debit Card',
  qr: 'DuitNow QR',
  cash: 'Cash'
};
function MethodIcon({
  method
}: {
  method: ShiftTxnMethod;
}) {
  if (method === 'card') return <CreditCardIcon size={16} className="text-blue-500" data-id="element-10916" />;
  if (method === 'qr') return <QrCodeIcon size={16} className="text-emerald-500" data-id="element-10917" />;
  return <BanknoteIcon size={16} className="text-amber-500" data-id="element-10918" />;
}
function getTxnStatusColor(status: ShiftTxnStatus) {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'refunded':
      return 'bg-amber-100 text-amber-800';
    case 'voided':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
export function ShiftDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'reconciliation'>('overview');
  const shift = mockShifts.find(s => s.id === id) || mockShifts[0];
  const getStatusColor = (status: ShiftStatus) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'variance':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending_settlement':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'settled':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const formatStatus = (status: string) => {
    return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };
  return <div className="max-w-[1400px] mx-auto space-y-6 pb-12" data-id="element-10919">
      {/* Back & Actions */}
      <div className="flex items-center justify-between" data-id="element-10920">
        <button onClick={() => navigate('/shifts')} className="flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors" data-id="element-10921">
          <ArrowLeftIcon size={16} className="mr-1" data-id="element-10922" />
          Back to Shifts
        </button>
        <div className="flex items-center gap-3" data-id="element-10923">
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-10924">
            Run X-Report
          </button>
          {shift.status === 'open' ? <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-id="element-10925">
              Close Shift
            </button> : <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-10926">
              Run Z-Report
            </button>}
        </div>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-10927">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6" data-id="element-10928">
          <div className="space-y-4" data-id="element-10929">
            <div className="flex items-center gap-3" data-id="element-10930">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-10931">{shift.id}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(shift.status)}`} data-id="element-10932">
                {formatStatus(shift.status)}
              </span>
              {shift.status === 'open' && <span className="flex items-center text-sm text-blue-600 font-medium" data-id="element-10933">
                  <span className="relative flex h-2 w-2 mr-2" data-id="element-10934">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" data-id="element-10935"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" data-id="element-10936"></span>
                  </span>
                  Live
                </span>}
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600" data-id="element-10937">
              <div data-id="element-10938">
                <span className="block text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-10939">
                  Store
                </span>
                <span className="font-medium text-gray-900" data-id="element-10940">{shift.store}</span>
              </div>
              <div data-id="element-10941">
                <span className="block text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-10942">
                  Terminal
                </span>
                <span className="font-medium text-gray-900" data-id="element-10943">
                  {shift.terminalId}
                </span>
              </div>
              <div data-id="element-10944">
                <span className="block text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-10945">
                  Operator
                </span>
                <div className="flex items-center gap-2" data-id="element-10946">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs" data-id="element-10947">
                    {shift.operator.charAt(0)}
                  </div>
                  <span className="font-medium text-gray-900" data-id="element-10948">
                    {shift.operator}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-8 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-10949">
            <div data-id="element-10950">
              <span className="block text-xs text-gray-500 mb-1" data-id="element-10951">Opened</span>
              <span className="font-medium text-gray-900" data-id="element-10952">
                {new Date(shift.openedAt).toLocaleString()}
              </span>
            </div>
            <div data-id="element-10953">
              <span className="block text-xs text-gray-500 mb-1" data-id="element-10954">Closed</span>
              <span className="font-medium text-gray-900" data-id="element-10955">
                {shift.closedAt ? new Date(shift.closedAt).toLocaleString() : '—'}
              </span>
            </div>
            <div data-id="element-10956">
              <span className="block text-xs text-gray-500 mb-1" data-id="element-10957">Duration</span>
              <span className="font-medium text-gray-900 flex items-center gap-1" data-id="element-10958">
                <ClockIcon size={14} className="text-gray-400" data-id="element-10959" />
                {shift.duration}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-10960">
        <nav className="flex space-x-8" data-id="element-10961">
          <button onClick={() => setActiveTab('overview')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-10962">
            Overview
          </button>
          <button onClick={() => setActiveTab('transactions')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'transactions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-10963">
            Transactions
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium" data-id="element-10964">
              {shift.transactions.length}
            </span>
          </button>
          <button onClick={() => setActiveTab('reconciliation')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'reconciliation' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-10965">
            Reconciliation
          </button>
        </nav>
      </div>

      {activeTab === 'overview' && <div className="space-y-6" data-id="element-10966">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-10967">
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-10968">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10969">
                Gross Sales
              </h3>
              <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-10970">
                {formatMoney(shift.grossValue)}
              </div>
              <div className="mt-2 text-xs text-green-600 font-medium" data-id="element-10971">
                +4.2% vs avg
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-10972">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10973">
                Net Sales
              </h3>
              <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-10974">
                {formatMoney(shift.netValue)}
              </div>
              <div className="mt-2 text-xs text-green-600 font-medium" data-id="element-10975">
                +3.8% vs avg
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-10976">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10977">
                Transactions
              </h3>
              <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-10978">
                {shift.txnCounts}
              </div>
              <div className="mt-2 text-xs text-gray-500 font-medium" data-id="element-10979">
                Avg ticket: {formatMoney(shift.grossValue / shift.txnCounts)}
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-10980">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10981">
                Refunds & Voids
              </h3>
              <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-10982">
                {formatMoney(shift.refundsValue)}
              </div>
              <div className="mt-2 text-xs text-gray-500 font-medium" data-id="element-10983">
                {shift.refundsCount} transactions
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-10984">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10985">
                Opening Float
              </h3>
              <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-10986">
                {formatMoney(shift.openingFloat)}
              </div>
              <div className="mt-2 text-xs text-gray-500 font-medium" data-id="element-10987">
                Cash in drawer
              </div>
            </div>
          </div>

          {/* Payment Methods Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-10988">
            <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-10989">
              Payment Methods
            </h2>
            <div className="space-y-4" data-id="element-10990">
              {[{
            method: 'Credit/Debit Card',
            value: 2450.0,
            count: 85,
            color: 'bg-blue-500'
          }, {
            method: 'DuitNow QR',
            value: 1200.5,
            count: 42,
            color: 'bg-emerald-500'
          }, {
            method: 'Cash',
            value: 450.0,
            count: 15,
            color: 'bg-amber-500'
          }].map((pm, i) => <div key={i} className="flex items-center gap-4" data-id="element-10991">
                  <div className="w-32 text-sm font-medium text-gray-700" data-id="element-10992">
                    {pm.method}
                  </div>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden" data-id="element-10993">
                    <div className={`h-full ${pm.color}`} style={{
                width: `${pm.value / shift.grossValue * 100}%`
              }} data-id="element-10994"></div>
                  </div>
                  <div className="w-32 text-right font-mono text-sm font-medium text-gray-900" data-id="element-10995">
                    {formatMoney(pm.value)}
                  </div>
                  <div className="w-16 text-right text-xs text-gray-500" data-id="element-10996">
                    {pm.count} txns
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {activeTab === 'transactions' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-10997">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-10998">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-10999">
              Transactions
            </h2>
            <span className="text-sm text-gray-500" data-id="element-11000">
              Showing {shift.transactions.length} of {shift.txnCounts}
            </span>
          </div>
          <div className="overflow-x-auto" data-id="element-11001">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-11002">
              <thead className="bg-gray-50" data-id="element-11003">
                <tr data-id="element-11004">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11005">
                    Transaction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11006">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11007">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11008">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11009">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11010">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100" data-id="element-11011">
                {shift.transactions.map(txn => <tr key={txn.id} className="hover:bg-gray-50" data-id="element-11012">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900" data-id="element-11013">
                      {txn.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-11014">
                      {new Date(txn.time).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-11015">
                      <span className="inline-flex items-center gap-2" data-id="element-11016">
                        <MethodIcon method={txn.method} data-id="element-11017" />
                        {METHOD_LABELS[txn.method]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500" data-id="element-11018">
                      {txn.reference}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11019">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getTxnStatusColor(txn.status)}`} data-id="element-11020">
                        {formatStatus(txn.status)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-mono font-medium ${txn.status === 'approved' ? 'text-gray-900' : 'text-red-600'}`} data-id="element-11021">
                      {txn.status === 'approved' ? '' : '-'}
                      {formatMoney(txn.amount)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {activeTab === 'reconciliation' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-11022">
          {/* Cash Reconciliation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-11023">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-11024">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-11025">
                Cash Reconciliation
              </h2>
            </div>
            <div className="p-6 space-y-6" data-id="element-11026">
              <div className="space-y-3" data-id="element-11027">
                <div className="flex justify-between text-sm" data-id="element-11028">
                  <span className="text-gray-600" data-id="element-11029">Opening Float</span>
                  <span className="font-mono text-gray-900" data-id="element-11030">
                    {formatMoney(shift.openingFloat)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11031">
                  <span className="text-gray-600" data-id="element-11032">Cash Sales</span>
                  <span className="font-mono text-gray-900" data-id="element-11033">
                    {formatMoney(450.0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11034">
                  <span className="text-gray-600" data-id="element-11035">Pay-ins</span>
                  <span className="font-mono text-gray-900" data-id="element-11036">
                    {formatMoney(shift.payIns)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11037">
                  <span className="text-gray-600" data-id="element-11038">Pay-outs</span>
                  <span className="font-mono text-gray-900" data-id="element-11039">
                    -{formatMoney(shift.payOuts)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-3 border-t border-gray-100" data-id="element-11040">
                  <span className="text-gray-900" data-id="element-11041">Expected Cash in Drawer</span>
                  <span className="font-mono text-gray-900" data-id="element-11042">
                    {formatMoney(shift.openingFloat + 450.0 + shift.payIns - shift.payOuts)}
                  </span>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200 space-y-4" data-id="element-11043">
                <div data-id="element-11044">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11045">
                    Declared / Counted Cash
                  </label>
                  <div className="relative" data-id="element-11046">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-mono" data-id="element-11047">
                      MYR
                    </span>
                    <input type="text" className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg font-mono text-gray-900 focus:ring-2 focus:ring-blue-500" defaultValue={(shift.openingFloat + 450.0 + shift.payIns - shift.payOuts + (shift.variance || 0)).toFixed(2)} disabled={shift.status !== 'open'} data-id="element-11048" />
                  </div>
                </div>

                {shift.variance !== null && <div className={`p-4 rounded-lg flex items-start gap-3 ${shift.variance === 0 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-id="element-11049">
                    {shift.variance === 0 ? <CheckCircle2Icon size={20} className="mt-0.5" data-id="element-11050" /> : <AlertCircleIcon size={20} className="mt-0.5" data-id="element-11051" />}
                    <div data-id="element-11052">
                      <h4 className="font-semibold" data-id="element-11053">
                        {shift.variance === 0 ? 'Balanced' : 'Variance Detected'}
                      </h4>
                      <p className="text-sm mt-1" data-id="element-11054">
                        {shift.variance === 0 ? 'Counted cash matches expected amount exactly.' : `Drawer is ${shift.variance > 0 ? 'over' : 'short'} by ${formatMoney(Math.abs(shift.variance))}.`}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          {/* Electronic Reconciliation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" data-id="element-11055">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-11056">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-11057">
                Electronic Reconciliation
              </h2>
            </div>
            <div className="p-6 space-y-6 flex-1" data-id="element-11058">
              <div className="space-y-3" data-id="element-11059">
                <div className="flex justify-between text-sm" data-id="element-11060">
                  <span className="text-gray-600" data-id="element-11061">
                    System Total (Card + QR)
                  </span>
                  <span className="font-mono text-gray-900" data-id="element-11062">
                    {formatMoney(3650.5)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11063">
                  <span className="text-gray-600" data-id="element-11064">
                    Acquirer Settlement Total
                  </span>
                  <span className="font-mono text-gray-900" data-id="element-11065">
                    {formatMoney(3650.5)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-3 border-t border-gray-100" data-id="element-11066">
                  <span className="text-gray-900" data-id="element-11067">Variance</span>
                  <span className="font-mono text-green-600" data-id="element-11068">
                    {formatMoney(0)}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-11069">
                <div className="flex items-center justify-between mb-2" data-id="element-11070">
                  <span className="text-sm font-medium text-gray-700" data-id="element-11071">
                    Matched Transactions
                  </span>
                  <span className="text-sm font-mono font-medium text-gray-900" data-id="element-11072">
                    127 / 127
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-11073">
                  <div className="bg-green-500 h-2 rounded-full" style={{
                width: '100%'
              }} data-id="element-11074"></div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 mt-auto" data-id="element-11075">
              <button className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" disabled={shift.status !== 'open'} data-id="element-11076">
                <LockIcon size={16} className="mr-2" data-id="element-11077" />
                Confirm & Close Shift
              </button>
            </div>
          </div>
        </div>}
    </div>;
}